import type {Practice} from "../components/PracticesMapContainer.tsx";
import {createContext, type Dispatch, type ReactNode, useContext, useReducer} from "react";

type PracticeWithInteractions = Practice & { noDelay?: boolean };

export interface AppState {
    selectedPractice?: PracticeWithInteractions,
    userPickedLocation?: google.maps.places.PlaceResult,
    isMobileDevice?: boolean,
    options: {
        practiceListWidth: number,
    }
    loading: boolean;
    error?: string;
}

type DeepPartial<T> = {
    [P in keyof T]?: T[P] extends object ? DeepPartial<T[P]> : T[P];
};

const initialState: AppState = {
    selectedPractice: undefined,
    userPickedLocation: undefined,
    options: {
        practiceListWidth: 508
    },
    loading: false,
    error: undefined
};


export const ActionTypes = {
    SET_SELECTED_PRACTICE: 'SET_SELECTED_PRACTICE',
    SET_OPTIONS: 'SET_OPTIONS',
    SET_USER_LOCATION: 'SET_USER_LOCATION',
    SET_LOADING: 'SET_LOADING',
    CLEAR_ERROR: 'CLEAR_ERROR',
    SET_ERROR: 'SET_ERROR',
    RESET_STATE: 'RESET_STATE'
} as const;

export type AppAction =
    | { type: typeof ActionTypes.SET_SELECTED_PRACTICE; payload: PracticeWithInteractions | undefined }
    | { type: typeof ActionTypes.SET_LOADING; payload: boolean }
    | { type: typeof ActionTypes.SET_OPTIONS; payload: AppState['options'] }
    | { type: typeof ActionTypes.SET_USER_LOCATION; payload: AppState['userPickedLocation'] }
    | { type: typeof ActionTypes.SET_ERROR; payload: string | undefined }
    | { type: typeof ActionTypes.CLEAR_ERROR }
    | { type: typeof ActionTypes.RESET_STATE };

function appReducer(state: AppState, action: AppAction): AppState {
    switch (action.type) {
        case ActionTypes.SET_SELECTED_PRACTICE:
            return {
                ...state,
                selectedPractice: action.payload
            };
        case ActionTypes.SET_OPTIONS:
            return {
                ...state,
                options: {...state.options, ...action.payload}
            };

        case ActionTypes.SET_USER_LOCATION:
            return {
                ...state,
                userPickedLocation: action.payload
            };

        case ActionTypes.SET_LOADING:
            return {
                ...state,
                loading: action.payload
            };

        case ActionTypes.SET_ERROR:
            return {
                ...state,
                error: action.payload,
                loading: false
            };

        case ActionTypes.CLEAR_ERROR:
            return {
                ...state,
                error: undefined
            };

        case ActionTypes.RESET_STATE:
            return initialState;

        default:
            return state;
    }
}

type AppStateContextType = AppState;
type AppDispatchContextType = Dispatch<AppAction>;

// Create contexts
const AppStateContext = createContext<AppStateContextType | undefined>(undefined);
const AppDispatchContext = createContext<AppDispatchContextType | undefined>(undefined);

type AppProviderProps = {
    children: ReactNode;
    defaultState?: DeepPartial<AppState>;
}

export function AppProvider({children, defaultState}: AppProviderProps): JSX.Element {
    const [state, dispatch] = useReducer(appReducer, {...initialState, ...(defaultState as AppState)});
    return (
        <AppStateContext.Provider value={state}>
            <AppDispatchContext.Provider value={dispatch}>
                {children}
            </AppDispatchContext.Provider>
        </AppStateContext.Provider>
    );
}


// eslint-disable-next-line react-refresh/only-export-components
export function useAppState(): AppState {
    const context = useContext(AppStateContext);
    if (!context) {
        throw new Error('useAppState must be used within an AppProvider');
    }
    return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAppDispatch(): Dispatch<AppAction> {
    const context = useContext(AppDispatchContext);
    if (!context) {
        throw new Error('useAppDispatch must be used within an AppProvider');
    }
    return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useApp(): {
    state: AppState;
    dispatch: Dispatch<AppAction>;
} {
    return {
        state: useAppState(),
        dispatch: useAppDispatch()
    };
}


// eslint-disable-next-line react-refresh/only-export-components
export const actions = {
    setSelectedPractice: (practice: AppState['selectedPractice']): AppAction => ({
        type: ActionTypes.SET_SELECTED_PRACTICE,
        payload: practice
    }),

    setUserLocation: (practice: AppState['userPickedLocation']): AppAction => ({
        type: ActionTypes.SET_USER_LOCATION,
        payload: practice
    }),

    setLoading: (loading: boolean): AppAction => ({
        type: ActionTypes.SET_LOADING,
        payload: loading
    }),

    setOptions: (options: AppState['options']): AppAction => ({
        type: ActionTypes.SET_OPTIONS,
        payload: options
    }),

    setError: (error?: string): AppAction => ({
        type: ActionTypes.SET_ERROR,
        payload: error
    }),

    clearError: (): AppAction => ({
        type: ActionTypes.CLEAR_ERROR
    }),

    resetState: (): AppAction => ({
        type: ActionTypes.RESET_STATE
    })
};